import { useParams, useNavigate } from "react-router-dom";
import { useAdeProfiles } from "@/hooks/useAdeProfiles";
import { useAdeDevices } from "@/hooks/useAdeDevices";
import { EnrollmentProfileWizard } from "@/components/enrollment/EnrollmentProfileWizard";
import { Loader2 } from "lucide-react";

export default function EditEnrollmentProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profiles, updateProfile, loading: profilesLoading } = useAdeProfiles();
  
  const profile = profiles.find((p) => p.id === id);
  
  const { getDevicesByProfile, loading: devicesLoading } = useAdeDevices(
    profile?.abm_connection_id
  );

  // Get IDs of devices currently assigned to this profile
  const assignedDeviceIds = profile?.profile_uuid
    ? getDevicesByProfile(profile.profile_uuid).map((d) => d.id)
    : [];

  const handleUpdate = async (profileData: any) => {
    if (!id) return profile;
    await updateProfile(id, profileData);
    return profile;
  };

  if (profilesLoading || devicesLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4">
        <h1 className="text-xl font-semibold">Profile Not Found</h1>
        <p className="text-muted-foreground">
          The enrollment profile you're looking for doesn't exist.
        </p>
        <button
          onClick={() => navigate("/profiles?tab=enrollment")}
          className="text-primary underline"
        >
          Back to Enrollment Profiles
        </button>
      </div>
    );
  }

  const initialData = {
    id: profile.id,
    customer_id: profile.customer_id || "",
    profile_name: profile.profile_name,
    description: profile.description || "",
    department: profile.department || "",
    abm_connection_id: profile.abm_connection_id,
    is_mandatory: profile.is_mandatory ?? true,
    is_mdm_removable: profile.is_mdm_removable ?? false,
    await_device_configured: profile.await_device_configured ?? false,
    auto_advance_setup: profile.auto_advance_setup ?? false,
    skip_setup_items: (profile.skip_setup_items as string[]) || [],
    support_phone_number: profile.support_phone_number || "",
    support_email_address: profile.support_email_address || "",
    configuration_web_url: profile.configuration_web_url || "",
    profile_uuid: profile.profile_uuid || undefined,
  };

  return (
    <EnrollmentProfileWizard
      mode="edit"
      initialData={initialData}
      initialSelectedDevices={assignedDeviceIds}
      onSave={handleUpdate}
    />
  );
}
